import React, { useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { Auth } from '~/amplify';
import sendPhoneVerificationCode from '~/amplify/utils/sendPhoneVerificationCode';
import enableSmsMfaPreference, {
  TotpRequiredForSmsError,
} from '~/amplify/utils/enableSmsMfaPreference';
import { PHONE_NUMBER_ATTRIBUTE_KEY } from '~/components/page/Auth/constants';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import { Body, Heading3 } from '~/components/atom/Typography';
import OTPInput from '~/components/molecule/OTPInput';
import PhoneVerificationField from '~/components/molecule/PhoneVerificationField';
import { Form, Field } from '~/components/organism/Forms';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import { reporter } from '~/hooks/useErrorReporter';
import PhoneValidation from '~/util/Validation/Phone';
import styled, { css } from 'styled-components';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import InfoBlock from '~/components/molecule/InfoBlock';
import Icon from '~/components/atom/Icon';
import confirmPhoneNumberAttribute from '~/amplify/utils/confirmPhoneNumberAttribute';
import { useProfileCognitoPhone } from '../../../../context/ProfileCognitoPhoneContext';
import Divider from '~/components/atom/Divider';
import Catalog from '~/Catalog';

const text = {
  addPhoneTitle: 'Backup telefoonnummer instellen',
  changePhoneTitle: 'Backup telefoonnummer wijzigen',
  explanation:
    'Zorg voor een geverifieerd backup telefoonnummer; zonder dit kun je je account niet herstellen als je geen toegang meer hebt tot je authenticator-app.',
  addInstruction: `Voer je backup telefoonnummer in.`,
  changeInstruction: `Voer je nieuwe backup telefoonnummer in. ${Catalog.mfaPhoneNumberFormatHint}`,
  confirmButton: 'Bevestigen',
  codeInstruction: 'Voer de verificatiecode in die je per SMS hebt ontvangen.',
  invalidPhone: Catalog.mfaInvalidDutchPhoneNumber,
  invalidCode: 'Ongeldige code. Probeer het opnieuw.',
  successBody: 'Je backup telefoonnummer is succesvol ingesteld.',
  cancelButton: 'Annuleren',
  samePhoneNumberAlreadyVerified:
    'Je backup telefoonnummer is al geverifieerd.',
  totpRequiredBody:
    'Je kunt dit nu niet inschakelen. Schakel eerst de authenticator-app in.',
  goToTotpSetup: 'Authenticator-app instellen',
  enableSmsRequiresTotp:
    'Schakel eerst de authenticator-app in voordat je SMS-verificatie kunt gebruiken.',
};

type Props = {
  onClose: () => void;
  alreadyEnabled?: boolean;
  onSmsEnabled: () => void;
  totpEnabled: boolean;
  onGoToTotpSetup: () => void;
};

const MFASetupPhoneModal: React.FC<Props> = ({
  onClose,
  alreadyEnabled = false,
  onSmsEnabled,
  totpEnabled,
  onGoToTotpSetup,
}) => {
  const {
    phoneNumber,
    phoneVerified,
    refresh: refreshCognitoPhone,
  } = useProfileCognitoPhone();
  const [smsSent, setSmsSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const addToast = useAddToast();

  const onSendCode = async (phone: string) => {
    const normalizedPhone = PhoneValidation.normalizeDutchPhoneNumber(phone);

    if (!normalizedPhone) {
      setPhoneError(text.invalidPhone);
      return false;
    }

    setSendingCode(true);
    setPhoneError(null);

    try {
      if (normalizedPhone === phoneNumber && phoneVerified) {
        addToast([
          formatToastMessage(text.samePhoneNumberAlreadyVerified, 'info'),
        ]);
        return true;
      }

      const result = await Auth.updateUserAttribute({
        userAttribute: {
          attributeKey: PHONE_NUMBER_ATTRIBUTE_KEY,
          value: normalizedPhone,
        },
      });

      await sendPhoneVerificationCode(result);
      await refreshCognitoPhone();
      setSmsSent(true);
      return true;
    } catch (err) {
      reporter.captureMessage(
        `MFASetupPhoneModal: Failed to update phone attribute: ${err}`,
        'error',
      );
      setPhoneError(text.invalidPhone);
      return false;
    } finally {
      setSendingCode(false);
    }
  };

  const onSubmit = async ({
    phone,
    code,
  }: {
    phone: string;
    code: string;
  }): Promise<{ [key: string]: string } | void> => {
    if (!smsSent) {
      await onSendCode(phone);
      return;
    }

    try {
      await confirmPhoneNumberAttribute(code);
      await enableSmsMfaPreference();
      await refreshCognitoPhone();
      onSmsEnabled();
      addToast([formatToastMessage(text.successBody, 'success')]);
      onClose();
    } catch (err) {
      if (err instanceof TotpRequiredForSmsError) {
        addToast([formatToastMessage(text.enableSmsRequiresTotp, 'danger')]);
        onGoToTotpSetup();
        return;
      }
      reporter.captureMessage(
        `MFASetupPhoneModal: Failed to confirm phone attribute: ${err}`,
        'error',
      );
      return { [FORM_ERROR]: text.invalidCode };
    }
  };

  if (!totpEnabled) {
    return (
      <ModalWrapper onClose={onClose} phoneNumber={null}>
        <JustificationContainer direction="column" gap="l" width="100%">
          <InfoBlock
            appearance="warning"
            padding={['base']}
            inline
            icon={{
              name: 'exclamation',
              backgroundColor: { group: 'warning' },
              color: { group: 'white' },
            }}
            headerText={null}
          >
            <Body size="base" margin={[null]}>
              {text.totpRequiredBody}
            </Body>
          </InfoBlock>

          <FormFooter
            justification="end"
            gap="base"
            align="center"
            width="100%"
          >
            <TextButton
              label={text.cancelButton}
              onClick={onClose}
              appearance="primary"
              size="medium"
            />
            <Button
              appearance="primary"
              size="medium"
              label={text.goToTotpSetup}
              onClick={onGoToTotpSetup}
            />
          </FormFooter>
        </JustificationContainer>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose} phoneNumber={phoneNumber}>
      {!phoneNumber && (
        <InfoBlock
          appearance={alreadyEnabled ? 'info' : 'warning'}
          padding={['base']}
          inline
          icon={{
            name: alreadyEnabled ? 'info' : 'exclamation',
            backgroundColor: alreadyEnabled
              ? { group: 'info' }
              : { group: 'warning' },
            color: { group: 'white' },
          }}
          headerText={null}
        >
          <Body size="base" margin={[null]}>
            {text.explanation}
          </Body>
        </InfoBlock>
      )}

      <Form initialValues={{ phone: '', code: '' }} onSubmit={onSubmit}>
        {({ handleSubmit, submitting, submitError: formError }) => (
          <StyledForm onSubmit={handleSubmit}>
            <JustificationContainer direction="column" gap="l" width="100%">
              <Body size="base" margin={[null]}>
                {phoneNumber ? text.changeInstruction : text.addInstruction}
              </Body>

              <PhoneVerificationField
                onSendCode={onSendCode}
                phoneError={phoneError}
                submitting={submitting}
                sendingCode={sendingCode}
                smsSent={smsSent}
              />

              {smsSent && (
                <JustificationContainer direction="column" gap="m" width="100%">
                  <Divider margin={['xxs', null, 's']} />

                  <Body size="base" margin={[null]}>
                    {text.codeInstruction}
                  </Body>
                  {formError && (
                    <Body
                      margin={[null]}
                      color={{ group: 'danger' }}
                      size="base"
                    >
                      {formError}
                    </Body>
                  )}
                  <Field name="code">
                    {({ input }) => (
                      <OTPInput
                        length={6}
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        disabled={submitting}
                        autoFocus
                      />
                    )}
                  </Field>
                </JustificationContainer>
              )}
            </JustificationContainer>

            <FormFooter justification="end" gap="base" align="center">
              <TextButton
                label={text.cancelButton}
                onClick={onClose}
                appearance="primary"
                size="medium"
              />
              {smsSent && (
                <Button
                  appearance="primary"
                  size="medium"
                  type="submit"
                  disabled={submitting}
                  loading={submitting}
                  label={text.confirmButton}
                />
              )}
            </FormFooter>
          </StyledForm>
        )}
      </Form>
    </ModalWrapper>
  );
};

const ModalWrapper = ({
  onClose,
  children,
  phoneNumber,
}: {
  onClose: () => void;
  children: React.ReactNode;
  phoneNumber: string | null;
}) => (
  <Overlay onClose={onClose}>
    <Modal onClose={onClose} maxWidth="520px">
      <JustificationContainer
        direction="column"
        gap="l"
        padding={['xl', 'xl', 'l', 'xl']}
        width="100%"
      >
        <JustificationContainer gap="s" width="100%" align="center">
          <Icon name="phone" strokeWidth={2.3} />
          <Heading3 size="m" margin={[null]}>
            {phoneNumber ? text.changePhoneTitle : text.addPhoneTitle}
          </Heading3>
        </JustificationContainer>
        {children}
      </JustificationContainer>
    </Modal>
  </Overlay>
);

const StyledForm = styled.form`
  width: 100%;
`;

const FormFooter = styled(JustificationContainer)(
  ({ theme }) => css`
    padding-top: ${theme.space('m')};
  `,
);

export default MFASetupPhoneModal;
